import { AxiosError } from 'axios';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClickAway } from 'react-use';

import { TranslationHistoryListItem, TranslationStatusListItem } from '../../../../api/model';
import { useGetProductionDetail } from '../../../../api/productions/productions';
import {
  getTranslationsCheckImportXml,
  PostTranslationsExportXmlMutationResult,
  useGetTranslations,
  usePostTranslationsBaseLanguageSync,
  usePostTranslationsDivertLanguageSync,
  usePostTranslationsExportXml,
  usePostTranslationsOtherLanguageIllustReplace,
} from '../../../../api/translations/translations';
import { useDefaultOptions, useOptionsForBinary } from '../../../../hooks/api';
import useContextMenu from '../../../../hooks/useContextMenu';
import { getAxiosRequestConfig } from '../../../../utils/api';
import { executeDownload } from '../../../../utils/download';
import ErrorComponent from '../../../atoms/ErrorComponent';
import WaitComponent from '../../../atoms/WaitComponent';
import BlobErrorModal from '../../../modules/BlobErrorModal';
import ConfirmModal from '../../../modules/ConfirmModal';
import ErrorModal from '../../../modules/ErrorModal';
import SlideToggle from '../../../modules/SlideToggle';
import SpinnerModal from '../../../modules/SpinnerModal';
import { Tooltip } from '../../../modules/Tooltip';
import { ProductionTranslationsDownloadModal } from './ProductionTranslationsDownloadModal';
import ProductionTranslationsExportModal from './ProductionTranslationsExportModal';
import ProductionTranslationsImportModal from './ProductionTranslationsImportModal';

interface Props {
  productionId: number;
  refetchFirstId: () => void;
}

/**
 * 翻訳タブ
 */
function ProductionTranslationsTab({ productionId, refetchFirstId }: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const options = useDefaultOptions(language);
  const axiosRequestConfig = getAxiosRequestConfig(language);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isHisOpen, setIsHisOpen] = useState(true); // 翻訳履歴一覧開閉
  const [isStaOpen, setIsStaOpen] = useState(true); // 翻訳状況一覧開閉

  // #region 画面表示
  const {
    data: translationResponse,
    isError,
    error,
    isLoading,
    refetch: refechTranslation,
  } = useGetTranslations(productionId, options);

  const productionDetailResponse = useGetProductionDetail(productionId, options);
  const productionDetail = productionDetailResponse.data?.data;
  // #endregion

  // #region ベース言語同期
  // ベース言語同期処理定義
  const [displayTranslationsBaseLanguageError, setDisplayTranslationsBaseLanguageError] = useState(false);
  const [displayConfirmSync, setDisplayConfirmSync] = useState(false); // ベース言語同期確認
  const {
    mutateAsync: translationsBaseLanguageSyncAsync,
    isLoading: translationBaseLanguageLoading,
    error: translationsBaseLanguageError,
  } = usePostTranslationsBaseLanguageSync(options);
  useEffect(() => {
    // setIsSubmitting(false);
    setDisplayTranslationsBaseLanguageError(translationsBaseLanguageError != null);
  }, [translationsBaseLanguageError]);

  // ベース言語同期時の処理
  const onSyncClicked = useCallback(async () => {
    // setIsSubmitting(true);
    await translationsBaseLanguageSyncAsync({ productionId });
    await refechTranslation();
    void refetchFirstId();
    // setIsSubmitting(false);
  }, [translationsBaseLanguageSyncAsync, productionId, refechTranslation, refetchFirstId]);
  // #endregion

  // #region 流用他言語同期
  const [otherLanguageProductionNumber, setOtherLanguageProductionNumber] = useState('');
  const [displayDivertLanguageSyncError, setDisplayDivertLanguageSyncError] = useState(false);
  const [displayConfirmDivertSync, setDisplayConfirmDivertSync] = useState(false); // 流用他言語同期確認
  const { mutateAsync: divertLanguageSyncAsync, error: divertLanguageSyncError } =
    usePostTranslationsDivertLanguageSync(options);
  useEffect(() => {
    setDisplayDivertLanguageSyncError(divertLanguageSyncError != null);
    setIsSubmitting(false);
  }, [divertLanguageSyncError]);

  // 流用他言語同期
  const onDivertLanguageSync = useCallback(async () => {
    setIsSubmitting(true);
    await divertLanguageSyncAsync({
      productionId,
      data: { production_no: otherLanguageProductionNumber },
    });
    await refechTranslation();
    setIsSubmitting(false);
  }, [divertLanguageSyncAsync, productionId, otherLanguageProductionNumber, refechTranslation]);
  // #endregion

  // #region 他言語画像差替え
  const [displayIllustRepError, setDisplayIllustRepError] = useState(false);
  const [displayConfirmIllustRep, setDisplayConfirmIllustRep] = useState(false); // 他言語画像差替え確認
  const {
    mutateAsync: illustRepAsync,
    isLoading: illustRepErrorIsLoading,
    error: illustRepError,
  } = usePostTranslationsOtherLanguageIllustReplace(options);
  useEffect(() => {
    setDisplayIllustRepError(illustRepError != null);
    // setIsSubmitting(false);
  }, [illustRepError]);

  // 他言語画像差替え時の処理
  const onIllustReplaceClicked = useCallback(async () => {
    // setIsSubmitting(true);
    await illustRepAsync({ productionId });
    await refechTranslation();
    // setIsSubmitting(false);
  }, [illustRepAsync, productionId, refechTranslation]);
  // #endregion

  // #region エクスポート／インポートダイアログ
  // インポートダイアログ表示
  const [showImportModal, setShowImportModal] = useState(false);
  const openImportModal = useCallback(() => {
    setShowImportModal(true);
  }, []);

  // エクスポートダイアログ表示
  const [forcedExportForModal, setForcedExportForModal] = useState<boolean>(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const openExportModal = useCallback((forcedExport: boolean) => {
    setForcedExportForModal(forcedExport);
    setShowExportModal(true);
  }, []);

  // インポート前チェック
  const [displayConfirmImport, setDisplayConfirmImport] = useState(false);
  const checkImportXml = useCallback(async () => {
    const checkImportXmlRes = await getTranslationsCheckImportXml(productionId, axiosRequestConfig);
    if (!checkImportXmlRes.data.translation_export_lock) {
      setDisplayConfirmImport(true);
    } else {
      void openImportModal();
    }
  }, [openImportModal, productionId, axiosRequestConfig]);
  // #endregion

  // #region 開発者用XML
  const optionsForBinary = useOptionsForBinary(language);
  const {
    mutateAsync: translationsExportXmlAsync,
    error: translationsExportXmlError,
    isLoading: translationsExportXmlIsLoading,
  } = usePostTranslationsExportXml<AxiosError<Blob>, PostTranslationsExportXmlMutationResult>(optionsForBinary);
  const [displayTranslationsExportXmlError, setDisplayTranslationsExportXmlError] = useState(false);

  useEffect(() => {
    setDisplayTranslationsExportXmlError(translationsExportXmlError != null);
  }, [translationsExportXmlError]);

  const onExportClicked = useCallback(async () => {
    const response = await translationsExportXmlAsync({
      productionId,
      data: { comment: '', forced_export: true, is_trans_tab: true },
    });
    if (response.data != null) {
      executeDownload(response);
    }
  }, [productionId, translationsExportXmlAsync]);

  // #endregion

  if (isLoading) {
    return <WaitComponent />;
  }
  if (isError) {
    if (error.response?.data.error_code === 'TranslationsNoPrivilege') {
      return (
        <div>
          <div style={{ fontSize: '14px' }}>{t('権限がないため翻訳タブを表示することができません')}</div>
        </div>
      );
    }
    return <ErrorComponent error={error} />;
  }

  return (
    <>
      {translationResponse.data.base_language_name && (
        <>
          <div className="mastr-head">
            <div className="inputfr labip3" style={{ display: 'flex', flexBasis: '100%' }}>
              <div style={{ width: '60%' }}>
                <label htmlFor="my3-1">{t('流用他言語資料管理No')}</label>{' '}
                <ProductionNumber
                  productionId={productionDetail?.production_basic_info?.other_language_production_id ?? 0}
                  productionNumber={otherLanguageProductionNumber}
                  setProductionNumber={setOtherLanguageProductionNumber}
                />
              </div>
              <div>
                <ul>
                  {/* ベース言語同期が実行されていたら、流用他言語同期は実行できない */}
                  <li className="modal-open">
                    {translationResponse.data.translation_operation_done === 4 ? (
                      <button type="button" className="text-button" style={{ color: '#9d9d9d', cursor: 'default' }}>
                        <i
                          className="fa-solid fa-arrows-rotate"
                          style={{ marginRight: '4px', color: '#9d9d9d', cursor: 'default' }}
                        />
                        {t('流用他言語同期')}
                      </button>
                    ) : (
                      <Tooltip ToolTipString={t('流用多言語資料管理Noに指定された原稿との同期処理を行う')}>
                        <button type="button" className="text-button" onClick={() => setDisplayConfirmDivertSync(true)}>
                          <i className="fa-solid fa-arrows-rotate" style={{ marginRight: '4px' }} />
                          {t('流用他言語同期')}
                        </button>
                      </Tooltip>
                    )}
                  </li>
                </ul>
              </div>
            </div>
            <div className="master-right-navi">
              <ul>
                <li>
                  <Tooltip ToolTipString={t('langrelation.csvの設定内容に従い、ベース言語の画像を差し替える')}>
                    <button type="button" className="text-button" onClick={() => setDisplayConfirmIllustRep(true)}>
                      <i className="fa-solid fa-file-image" />
                      {t('他言語画像差替え')}
                      {translationResponse.data.replace_illust_done && t('(実行済)')}
                    </button>
                  </Tooltip>
                </li>
              </ul>
            </div>
            <div className="master-right-navi">
              <ul>
                {/* 流用他言語同期が実行されていたら、ベース言語同期は実行できない */}
                {/*  
                <li>
                  {translationResponse.data.translation_operation_done === 5 ? (
                    <button type="button" className="text-button" style={{ color: '#9d9d9d', cursor: 'default' }}>
                      <i className="fa-solid fa-arrows-rotate" style={{ color: '#9d9d9d', cursor: 'default' }} />
                      {t('ベース言語同期')}
                    </button>
                  ) : (
                    <Tooltip ToolTipString={t('ベース言語と同期を行い、翻訳済みのデータは元に戻す。')}>
                      <button
                        type="button"
                        className="text-button"
                        onClick={() => !translationBaseLanguageLoading && setDisplayConfirmSync(true)}
                      >
                        <i
                          className="fa-solid fa-arrows-rotate"
                          style={{
                            color: !translationBaseLanguageLoading ? '' : '#9d9d9d',
                            cursor: !translationBaseLanguageLoading ? 'pointer' : 'default',
                          }}
                        />
                        {t('ベース言語同期')}
                      </button>
                    </Tooltip>
                  )}
                </li>
                */}
                <li>
                  <Tooltip ToolTipString={t('ベース言語と同期を行い、翻訳済みのデータは元に戻す')}>
                    <button
                      type="button"
                      className="text-button"
                      onClick={() => !translationBaseLanguageLoading && setDisplayConfirmSync(true)}
                    >
                      <i
                        className="fa-solid fa-arrows-rotate"
                        style={{
                          color: !translationBaseLanguageLoading ? '' : '#9d9d9d',
                          cursor: !translationBaseLanguageLoading ? 'pointer' : 'default',
                        }}
                      />
                      {t('ベース言語同期')}
                    </button>
                  </Tooltip>
                </li>
              </ul>
            </div>
          </div>
          <div className="master-navi">
            <ul>
              <li>
                <button type="button" className="text-button" onClick={() => checkImportXml()}>
                  <i className="fa-solid fa-upload" />
                  {t('インポート')}
                </button>
              </li>
              <li>
                <button type="button" className="text-button" onClick={() => openExportModal(false)}>
                  <i className="fa-solid fa-download" />
                  {t('エクスポート')}
                </button>
              </li>
            </ul>
          </div>
        </>
      )}
      <div className="master-navi" style={{ marginTop: '50px' }}>
        <ul>
          <li>
            <span style={{ fontWeight: 'bold' }}>{t('言語')}：</span>
            {translationResponse.data.language_name}
          </li>
          <li>
            <span style={{ fontWeight: 'bold' }}>{t('ベース言語')}：</span>
            {translationResponse.data.base_language_name}
          </li>
        </ul>
      </div>
      <div className="manuscript mt20">
        <h3 className="mdh2">
          <button
            type="button"
            className="text-button"
            onClick={(e) => {
              e.preventDefault();
              setIsHisOpen((pre) => !pre);
            }}
          >
            <span>
              {t('翻訳履歴')}
              <span className="num">{translationResponse.data.translationHistoryList?.length}</span>
              <i className={`fa-solid ${isHisOpen ? 'fa-chevron-down' : 'fa-chevron-up'}`} />
            </span>
          </button>
        </h3>
        <SlideToggle open={isHisOpen ?? false}>
          <HistoryTable translationHistoryList={translationResponse.data.translationHistoryList} />
        </SlideToggle>
      </div>
      <div className="manuscript mt20">
        <h3 className="mdh2">
          <button
            type="button"
            className="text-button"
            onClick={(e) => {
              e.preventDefault();
              setIsStaOpen((pre) => !pre);
            }}
          >
            <span>
              {t('翻訳状況')}
              <i className={`fa-solid ${isStaOpen ? 'fa-chevron-down' : 'fa-chevron-up'}`} />
            </span>
          </button>
        </h3>
        <SlideToggle open={isStaOpen ?? false}>
          <StatusTable translationStatusList={translationResponse.data.translationStatusList} />
        </SlideToggle>
      </div>

      <div className="master-navi mt20">
        <ul style={{ justifyContent: 'flex-end' }}>
          <li>
            <button type="button" className="text-button" onClick={() => onExportClicked()}>
              <i className="fa-solid fa-download" />
              {t('開発者用XML')}
            </button>
          </li>
        </ul>
      </div>

      <ProductionTranslationsImportModal
        active={showImportModal}
        productionId={productionId}
        onClose={async () => {
          setShowImportModal(false);
          setIsSubmitting(true);
          await refechTranslation();
          setIsSubmitting(false);
        }}
      />
      <ProductionTranslationsExportModal
        active={showExportModal}
        productionId={productionId}
        forcedExport={forcedExportForModal}
        onClose={async () => {
          setShowExportModal(false);
          setIsSubmitting(true);
          await refechTranslation();
          setIsSubmitting(false);
        }}
      />
      {isSubmitting && <SpinnerModal />}
      {translationBaseLanguageLoading && <SpinnerModal />}
      {illustRepErrorIsLoading && <SpinnerModal />}
      {displayTranslationsBaseLanguageError && (
        <ErrorModal
          onClickOk={() => {
            // setIsSubmitting(false);
            setDisplayTranslationsBaseLanguageError(false);
          }}
          error={translationsBaseLanguageError}
        />
      )}
      {displayDivertLanguageSyncError && (
        <ErrorModal
          onClickOk={() => {
            setIsSubmitting(false);
            setDisplayDivertLanguageSyncError(false);
          }}
          error={divertLanguageSyncError}
        />
      )}
      {displayIllustRepError && (
        <ErrorModal
          onClickOk={() => {
            setIsSubmitting(false);
            setDisplayIllustRepError(false);
          }}
          error={illustRepError}
        />
      )}
      {displayConfirmImport && (
        <ConfirmModal
          onClickCancel={() => setDisplayConfirmImport(false)}
          onClickOk={() => {
            setDisplayConfirmImport(false);
            void openImportModal();
          }}
        >
          {t('エクスポート処理がされていませんが、インポート処理を行ないますか?')}
        </ConfirmModal>
      )}
      {displayConfirmSync && (
        <ConfirmModal
          onClickCancel={() => setDisplayConfirmSync(false)}
          onClickOk={() => {
            setDisplayConfirmSync(false);
            void onSyncClicked();
          }}
        >
          {t('ベース言語同期を行いますか?')}
        </ConfirmModal>
      )}
      {displayConfirmIllustRep && (
        <ConfirmModal
          onClickCancel={() => setDisplayConfirmIllustRep(false)}
          onClickOk={() => {
            setDisplayConfirmIllustRep(false);
            void onIllustReplaceClicked();
          }}
        >
          {t('他言語画像差替え処理を行いますか?')}
        </ConfirmModal>
      )}
      {displayConfirmDivertSync && (
        <ConfirmModal
          onClickCancel={() => setDisplayConfirmDivertSync(false)}
          onClickOk={() => {
            setDisplayConfirmDivertSync(false);
            void onDivertLanguageSync();
          }}
        >
          {t('流用他言語同期を行いますか?')}
        </ConfirmModal>
      )}

      {translationsExportXmlIsLoading && <SpinnerModal />}
      {displayTranslationsExportXmlError && (
        <BlobErrorModal
          onClickOk={() => {
            setDisplayTranslationsExportXmlError(false);
          }}
          error={translationsExportXmlError}
        />
      )}
    </>
  );
}

export default ProductionTranslationsTab;

interface ProductionNumberProps {
  productionId: number;
  productionNumber: string;
  setProductionNumber: Dispatch<SetStateAction<string>>;
}

function ProductionNumber({ productionId, productionNumber, setProductionNumber }: ProductionNumberProps) {
  const {
    i18n: { language },
  } = useTranslation();

  const options = useDefaultOptions(language);
  const { data: productionDetailResponse, isLoading, isError, error } = useGetProductionDetail(productionId, options);
  const productionDetail = productionDetailResponse?.data;

  useEffect(() => {
    setProductionNumber(productionDetail?.production_basic_info?.production_no ?? '');
  }, [productionDetail, setProductionNumber]);

  if (productionId !== 0) {
    if (isLoading) {
      return <WaitComponent />;
    }

    if (isError) {
      return <ErrorComponent error={error} />;
    }
  }

  return (
    <input
      type="text"
      id="my3-1"
      name=""
      value={productionNumber}
      onChange={(e) => setProductionNumber(e.target.value)}
    />
  );
}

// #region 履歴テーブル
interface HistoryTableProps {
  translationHistoryList: TranslationHistoryListItem[] | undefined;
}

function HistoryTable({ translationHistoryList }: HistoryTableProps) {
  const { t } = useTranslation();

  const [sortInfo, setSortInfo] = useState({ sortkey: '' as keyof TranslationHistoryListItem, isDesc: false });
  const translationHistoryListItems = useMemo(
    () =>
      (translationHistoryList ?? []).sort((a, b) => {
        const propertyValueA = a[sortInfo.sortkey] ?? '';
        const propertyValueB = b[sortInfo.sortkey] ?? '';
        const direction = sortInfo.isDesc ? -1 : 1;

        if (propertyValueA < propertyValueB) {
          return -1 * direction;
        }
        if (propertyValueA > propertyValueB) {
          return 1 * direction;
        }
        return 0;
      }),
    [translationHistoryList, sortInfo.isDesc, sortInfo.sortkey]
  );

  // ソートクリック時の処理
  const onSortClicked = useCallback((key: keyof TranslationHistoryListItem) => {
    setSortInfo((preSortInfo) => ({ sortkey: key, isDesc: preSortInfo.sortkey === key && !preSortInfo.isDesc }));
  }, []);

  return (
    <table className="table16 in-elloutput2">
      <thead>
        <tr>
          <th style={{ width: '100px' }}>
            {t('処理日')}
            <button type="button" className="text-button" onClick={() => onSortClicked('created_at')}>
              <i className="fa-solid fa-sort" />
            </button>
          </th>
          <th style={{ width: '140px' }}>
            {t('翻訳操作区分')}
            <button
              type="button"
              className="text-button"
              onClick={() => onSortClicked('translation_operation_division_name')}
            >
              <i className="fa-solid fa-sort" />
            </button>
          </th>
          <th style={{ width: '100px' }}>
            {t('ユーザー名')}
            <button type="button" className="text-button" onClick={() => onSortClicked('user_name')}>
              <i className="fa-solid fa-sort" />
            </button>
          </th>
          <th>
            {t('コメント')}
            <button type="button" className="text-button" onClick={() => onSortClicked('comment')}>
              <i className="fa-solid fa-sort" />
            </button>
          </th>
          <th style={{ width: '100px' }}>
            {t('未翻訳件数')}
            <button type="button" className="text-button" onClick={() => onSortClicked('not_translated_count')}>
              <i className="fa-solid fa-sort" />
            </button>
          </th>
          <th style={{ width: '100px' }}>
            {t('翻訳済件数')}
            <button type="button" className="text-button" onClick={() => onSortClicked('translated_count')}>
              <i className="fa-solid fa-sort" />
            </button>
          </th>
          <th>
            {t('ファイル名')}
            <button type="button" className="text-button" onClick={() => onSortClicked('file_name')}>
              <i className="fa-solid fa-sort" />
            </button>
          </th>
        </tr>
      </thead>
      <tbody>
        {translationHistoryListItems.map((item) => (
          <TranslationHistoryListRow key={item.translation_history_id} translationListItem={item} />
        ))}
      </tbody>
    </table>
  );
}
// #endregion

// #region 履歴一覧行
interface TranslationHistoryListRowProps {
  translationListItem: TranslationHistoryListItem;
}

/**
 * 翻訳履歴一覧行
 * @param param0
 * @returns
 */
function TranslationHistoryListRow({ translationListItem }: TranslationHistoryListRowProps) {
  const { points, setPoints } = useContextMenu();
  const [contextOpen, setContextOpen] = useState(false);

  const ref = useRef(null);
  useClickAway(ref, () => {
    setContextOpen(false);
  });

  const handleContextMenu = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    e.preventDefault();
    setContextOpen(true);
  };

  return (
    <tr>
      <td>{translationListItem.created_at}</td>
      <td>{translationListItem.translation_operation_division_name}</td>
      <td>{translationListItem.user_name}</td>
      <td>{translationListItem.comment}</td>
      <td>{translationListItem.not_translated_count}</td>
      <td>{translationListItem.translated_count}</td>
      <td>
        {translationListItem.file_name != null && (
          <span className="manualpopfr">
            <span
              className="grayfr"
              onContextMenu={(event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
                event.preventDefault();
                setPoints({ x: event.pageX - window.scrollX, y: event.pageY - window.scrollY });
                handleContextMenu(event);
              }}
            >
              {translationListItem.file_name}
            </span>
            <ul
              className="pop"
              style={{
                display: contextOpen ? 'block' : '',
                position: 'fixed',
                top: `${points.y}px`,
                left: `${points.x}px`,
                width: '120px',
              }}
              ref={ref}
            >
              <ProductionTranslationsDownloadModal
                translationHistoryId={Number(translationListItem.translation_history_id)}
              />
            </ul>
          </span>
        )}
      </td>
    </tr>
  );
}
// #endregion

// #region 状況テーブル
interface StatusTableProps {
  translationStatusList: TranslationStatusListItem[] | undefined;
}

/**
 * 翻訳状態
 * @param param0
 * @returns
 */
function StatusTable({ translationStatusList }: StatusTableProps) {
  const { t } = useTranslation();

  const [sortInfo, setSortInfo] = useState({ sortkey: '' as keyof TranslationStatusListItem, isDesc: false });
  const translationStatusListItems = useMemo(
    () =>
      (translationStatusList ?? []).sort((a, b) => {
        const propertyValueA = a[sortInfo.sortkey] ?? '';
        const propertyValueB = b[sortInfo.sortkey] ?? '';
        const direction = sortInfo.isDesc ? -1 : 1;

        if (propertyValueA < propertyValueB) {
          return -1 * direction;
        }
        if (propertyValueA > propertyValueB) {
          return 1 * direction;
        }
        return 0;
      }),
    [translationStatusList, sortInfo.isDesc, sortInfo.sortkey]
  );

  // ソートクリック時の処理
  const onSortClicked = useCallback((key: keyof TranslationStatusListItem) => {
    setSortInfo((preSortInfo) => ({ sortkey: key, isDesc: preSortInfo.sortkey === key && !preSortInfo.isDesc }));
  }, []);

  return (
    <table className="table16 in-elloutput2">
      <thead>
        <tr>
          <th>
            {t('レベル')}
            <button type="button" className="text-button" onClick={() => onSortClicked('level')}>
              <i className="fa-solid fa-sort" />
            </button>
          </th>
          <th>
            {t('タイトル')}
            <button type="button" className="text-button" onClick={() => onSortClicked('title')}>
              <i className="fa-solid fa-sort" />
            </button>
          </th>
          <th>
            {t('対象外')}
            <button type="button" className="text-button" onClick={() => onSortClicked('status_0')}>
              <i className="fa-solid fa-sort" />
            </button>
          </th>
          <th>
            {t('未翻訳')}
            <button type="button" className="text-button" onClick={() => onSortClicked('status_1')}>
              <i className="fa-solid fa-sort" />
            </button>
          </th>
          <th>
            {t('翻訳中')}
            <button type="button" className="text-button" onClick={() => onSortClicked('status_2')}>
              <i className="fa-solid fa-sort" />
            </button>
          </th>
          <th>
            {t('翻訳済')}
            <button type="button" className="text-button" onClick={() => onSortClicked('status_3')}>
              <i className="fa-solid fa-sort" />
            </button>
          </th>
          {/* 
          <th>
            {t('翻訳不要')}
            <button type="button" className="text-button" onClick={() => onSortClicked('status_4')}>
              <i className="fa-solid fa-sort" />
            </button>
          </th>
          */}
        </tr>
      </thead>
      <tbody>
        {translationStatusListItems.map((item) => (
          <tr key={item.translation_id}>
            <td>{item.level}</td>
            <td>{item.title}</td>
            <td>{item.status_0}</td>
            <td>{item.status_1}</td>
            <td>{item.status_2}</td>
            <td>{item.status_3}</td>
            {/*
            <td>{item.status_4}</td>
            */}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
// #endregion
